/**
 * Exports and transcript actions for the session on screen: analysis PDF
 * download (standard / observation), open the source transcript, and share
 * it (school members).
 */
import { useCallback, useState } from 'react';

import { showDialog } from '@/utils/dialogs';
import { chatAPI, transcriptionAPI } from '../../services/api';
import type { ChatSession } from '../../types';
import { buildAnalysisPdfFilename } from './chatConstants';
import { errorStatusOf } from './chatErrors';
import type { SetState, TFn } from './chatFlowTypes';

export function useSessionExports({
  language,
  t,
  sessions,
  currentSessionId,
  getSessionSourceFilename,
  viewingTranscript,
  setViewingTranscript,
  setShowTranscriptViewer,
}: {
  language: string;
  t: TFn;
  sessions: ChatSession[];
  currentSessionId: number | null;
  getSessionSourceFilename: (session?: ChatSession | null) => string;
  viewingTranscript: any;
  setViewingTranscript: SetState<any>;
  setShowTranscriptViewer: SetState<boolean>;
}) {
  const [shareTranscriptId, setShareTranscriptId] = useState<number | null>(null);
  const [shareTranscriptName, setShareTranscriptName] = useState('');
  const [shareTranscriptModalVisible, setShareTranscriptModalVisible] = useState(false);

  const handleExportAnalysisPdf = async (
    sessionId: number,
    reportType: 'standard' | 'observation' = 'standard',
  ) => {
    try {
      const pdfBlob = await chatAPI.exportSessionPdf(sessionId, language, reportType);
      const session = sessions.find((item) => item.id === sessionId);
      const baseName = buildAnalysisPdfFilename(session, language, sessionId);
      const filename = reportType === 'observation'
        ? baseName.replace(/\.pdf$/i, '-observation.pdf')
        : baseName;

      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      // 409 = the session has no analysis to export (CHAT-19: translated,
      // not the backend's English detail).
      if (errorStatusOf(error) === 409) {
        showDialog(t('chatLibrary.errors.noAnalysisToExport'));
      } else {
        const message = t('chat.exportPdfFailed');
        showDialog(message);
      }
    }
  };

  const getCurrentSourceDocumentId = useCallback(() => {
    if (viewingTranscript?.id) return Number(viewingTranscript.id);
    if (!currentSessionId) return null;
    const currentSession = sessions.find((session) => session.id === currentSessionId);
    return currentSession?.source_document_id ?? null;
  }, [currentSessionId, sessions, viewingTranscript]);

  const openCurrentTranscript = useCallback(async () => {
    const documentId = getCurrentSourceDocumentId();
    if (!documentId) return;
    try {
      const transcript = viewingTranscript?.id === documentId
        ? viewingTranscript
        : await transcriptionAPI.getContent(documentId);
      setViewingTranscript(transcript);
      setShowTranscriptViewer(true);
    } catch (error) {
      console.warn('Failed to open session transcript:', error);
      showDialog(t('transcription.errors.loadFailed'));
    }
  }, [getCurrentSourceDocumentId, t, viewingTranscript, setShowTranscriptViewer, setViewingTranscript]);

  const openShareCurrentTranscript = useCallback(() => {
    const documentId = getCurrentSourceDocumentId();
    if (!documentId) return;
    const currentSession = currentSessionId
      ? sessions.find((session) => session.id === currentSessionId)
      : undefined;
    setShareTranscriptId(documentId);
    setShareTranscriptName(getSessionSourceFilename(currentSession) || viewingTranscript?.original_filename || currentSession?.title || 'transcript');
    setShareTranscriptModalVisible(true);
  }, [currentSessionId, getCurrentSourceDocumentId, getSessionSourceFilename, sessions, viewingTranscript]);

  return {
    shareTranscriptId,
    shareTranscriptName,
    shareTranscriptModalVisible,
    setShareTranscriptModalVisible,
    handleExportAnalysisPdf,
    getCurrentSourceDocumentId,
    openCurrentTranscript,
    openShareCurrentTranscript,
  };
}
